<script>
import baseMixin from '@/mixins/baseMixin'
import timesheetMixin from '@/mixins/timesheetMixin'
import DateFormatToken from '@/shared/constants/date/DateFormatToken'
import { BookingStatus } from '@/shared/constants/booking/BookingStatus'

export default {
  name: 'TimesheetChangeTimeCardDetails',
  mixins: [baseMixin, timesheetMixin],
  props: {
    timesheet: {
      type: Object,
      required: true,
    },
  },
  computed: {
    compBookingDate() {
      return this.renderDate(
        this.timesheet.startTimeUTC,
        this.timesheet.locationTimeZone,
        DateFormatToken.ddddDDMMMM
      )
    },
    candidateHasModifiedTimesheetOrStatedDidNotHappen() {
      return this.hasCandidateModifiedTimesheet(this.timesheet)
    },
    candidateStatedBookingDidNotHappen() {
      return (
        this.timesheet.bookingStatus ===
        BookingStatus.CANDIDATE_SUBMITTED_TIMESHEET_DID_NOT_HAPPEN
      )
    },
    candidateHasModifiedTimesheet() {
      return (
        this.timesheet.bookingStatus ===
        BookingStatus.CANDIDATE_SUBMITTED_TIMESHEET_MODIFIED
      )
    },
    compModifiedBookingTimeString() {
      if (this.candidateStatedBookingDidNotHappen) {
        return this.$t(
          'timesheets.changeTimeDialog.candidateChangesDidNotHappen',
          [this.timesheet.candidateFirstName]
        )
      }
      const bookingDetails = this.getModifiedOrOriginalBookingDetails(
        this.timesheet
      )
      return `${bookingDetails.startTime} - ${bookingDetails.endTime}`
    },
    compModifiedBreaksString() {
      const bookingDetails = this.getModifiedOrOriginalBookingDetails(
        this.timesheet
      )
      return this.$t('timesheets.changeTimeDialog.breakMinsText', [
        bookingDetails.breaks,
      ])
    },
    compOriginalTooltip() {
      return this.$t('timesheets.table.time.originalBookingTimeTooltip', [
        `${this.compOriginalBookingTimeString} ${this.compOriginalBreaksString}`,
      ])
    },
    compOriginalBookingTimeString() {
      const bookingDetails = this.getOriginalBookingDetails(this.timesheet)
      return `${bookingDetails.startTime} - ${bookingDetails.endTime}`
    },
    compOriginalBreaksString() {
      const bookingDetails = this.getOriginalBookingDetails(this.timesheet)
      return this.$t('timesheets.changeTimeDialog.breakMinsText', [
        bookingDetails.breaks,
      ])
    },
    compModifiedTooltip() {
      return this.$t('timesheets.changeTimeDialog.candidateChangesTooltip', [
        this.timesheet.candidateFirstName,
      ])
    },
  },
}
</script>

<template>
  <div>
    <p class="font-weight-bold">{{ timesheet.candidateFullName }}</p>

    <p class="font-weight-medium">{{ timesheet.locationName }}</p>

    <p class="mb-0 font-weight-medium">
      {{ compBookingDate }}
    </p>

    <div class="d-flex mb-6 justify-start align-center">
      <div class="cursor-help">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <div v-on="on">
              <p
                data-testid="timesheet-change-time-card-details-original-times"
                :class="[
                  'mb-0 ws-pre-wrap',
                  candidateHasModifiedTimesheetOrStatedDidNotHappen
                    ? 'opacity-8 text-decoration-line-through'
                    : '',
                ]"
                >{{ compOriginalBookingTimeString }}</p
              >
              <p
                data-testid="timesheet-change-time-card-details-original-breaks-text"
                :class="[
                  'mb-0 ws-pre-wrap caption',
                  candidateHasModifiedTimesheetOrStatedDidNotHappen
                    ? 'opacity-8 text-decoration-line-through'
                    : '',
                ]"
                >{{ compOriginalBreaksString }}</p
              >
            </div>
          </template>
          <span class="ws-pre-wrap">{{ compOriginalTooltip }}</span>
        </v-tooltip>
      </div>
      <div
        v-if="candidateHasModifiedTimesheetOrStatedDidNotHappen"
        style="font-size: 40px;"
        class="mx-6"
      >
        &#10230;
      </div>
      <div
        v-if="candidateHasModifiedTimesheetOrStatedDidNotHappen"
        class="cursor-help"
      >
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <div v-on="on">
              <p
                data-testid="timesheet-change-time-card-details-modified-times"
                class="mb-0 ws-pre-wrap"
                >{{ compModifiedBookingTimeString }}</p
              >
              <p
                v-if="!candidateStatedBookingDidNotHappen"
                data-testid="timesheet-change-time-card-details-modified-breaks-text"
                class="caption mb-0"
                >{{ compModifiedBreaksString }}</p
              >
            </div>
          </template>
          <span class="ws-pre-wrap">{{ compModifiedTooltip }}</span>
        </v-tooltip>
      </div>
    </div>
  </div>
</template>
